// Reads the frame-number barcode burned into T-14/T-17 clips (layout: test-assets/scripts/barcode.mjs).
// Block size S = round(32 * H / 1080); 16 bits MSB first; bit i at x = 2S + i*S, y = 2S, size S×S.

import type { DecodedFrame } from '../media/protocol';

export function barcodeBlockSize(height: number): number {
  return Math.round((32 * height) / 1080);
}

export function lumaGetter(f: DecodedFrame): { get: (x: number, y: number) => number; max: number } {
  const l0 = f.layout[0];
  if (f.format === 'RGBA16') {
    const words = new Uint16Array(f.data);
    return { get: (x, y) => words[(l0.offset + y * l0.stride) / 2 + x * 4 + 1], max: 65535 };
  }
  if (f.format === 'RGBA') {
    const bytes = new Uint8Array(f.data);
    return { get: (x, y) => bytes[l0.offset + y * l0.stride + x * 4 + 1], max: 255 };
  }
  if (f.format.includes('P1')) {
    const words = new Uint16Array(f.data);
    const bd = f.format.includes('P12') ? 12 : 10;
    return { get: (x, y) => words[(l0.offset + y * l0.stride) / 2 + x], max: 2 ** bd - 1 };
  }
  const bytes = new Uint8Array(f.data);
  return { get: (x, y) => bytes[l0.offset + y * l0.stride + x], max: 255 };
}

export function readBarcode(f: DecodedFrame): number {
  const S = barcodeBlockSize(f.height);
  const { get, max } = lumaGetter(f);
  const threshold = max / 2;
  let value = 0;
  for (let i = 0; i < 16; i++) {
    const x0 = 2 * S + i * S;
    const y0 = 2 * S;
    let sum = 0;
    let n = 0;
    for (let y = y0 + (S >> 2); y < y0 + S - (S >> 2); y++) {
      for (let x = x0 + (S >> 2); x < x0 + S - (S >> 2); x++) {
        sum += get(x, y);
        n++;
      }
    }
    value = (value << 1) | (sum / n > threshold ? 1 : 0);
  }
  return value;
}
